import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../redux/store';
import {
  TextField,
  Autocomplete,
  Button,
  Snackbar,
  MenuItem,
  Select,
  FormControl,
  InputLabel, Grid, Avatar
} from '@mui/material';
import { editLeague } from '../../redux/slices/league';
import { useLocation, useNavigate } from 'react-router-dom';
import MuiAlert, { AlertProps } from '@mui/material/Alert';
import moment from 'moment';
import styles from './leagues.module.scss';
import CheckCircleTwoToneIcon from '@mui/icons-material/CheckCircleTwoTone';
import DoDisturbOnTwoToneIcon from '@mui/icons-material/DoDisturbOnTwoTone';
import EmojiEventsTwoToneIcon from '@mui/icons-material/EmojiEventsTwoTone';
import SportsSoccerTwoToneIcon from '@mui/icons-material/SportsSoccerTwoTone';
import { getAvailableRegions } from '../../api/settings';
import { createPreSignedUrl, getImagesList } from '../../api/files';
import clsx from 'clsx';
import ImageTwoToneIcon from '@mui/icons-material/ImageTwoTone';
import { ImageType } from '../mediaLibrary';

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const freeSoloOptions = ['image', 'id' ,'name', 'shortName', 'createdDate', 'sortPriority', 'matchesCount', 'price'];

export const League = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState({ action: false, text: "" });
  const [isOpenFileSelect, setIsOpenFileSelect] = useState(false);
  const [availableImages, setAvailableImages] = useState([]);
  
  const [options, setOptions] = useState<any>({
    id: [],
    activated: [true, false],
    color: ["#11966E", "#2C7B42", "#005943", "#0084AD", "#2974D6", "#1C4BC2", "#1D2F8F", "#3C249A", "#465354"],
    name: [],
    shortName: [],
    sortPriority: [],
    createdDate: [""],
    isPaid: [true, false],
    region: [],
    type: ["championship", "tournament"]
  });
  
  const [newLeague, setNewLeague] = useState<any>({
    ...location.state,
    type: location.state.type || "",
    sortPriority: location.state.sortPriority || 0
  });
  
  useEffect(() => {
    (async () => {
      setAvailableImages(await getImagesList({ type: 'LEAGUE_LOGO', take: 50 }));
    })()
  }, []);

  const handleChangeValue = (event: any, item: string) => {
    if (item === 'region') {
      const updatedRegion = options.region.find((item: { name: string; id: string }) =>
        item.name === event.target.value);
      const newObj: any = { ...newLeague, region: { ...updatedRegion}};
      setNewLeague(newObj);
    } else {
      const newObj: any = { ...newLeague };
      newObj[item] = item === 'sortPriority' || item === 'price' ? Number(event.target.value) : event.target.value;
      setNewLeague(newObj);
    }
  };

  const handleSave = async () => {
    try {
      const updatedLeagueParams = {...newLeague, regionId: newLeague?.region?.id};

      if (!newLeague.region) {
        setError({ action: true, text: "Region is not valid" });
      } else if (!options.type.includes(newLeague.type)) {
        setError({ action: true, text: "Type is not valid" });
      } else {
        setError({ action: false, text: "" });
        delete updatedLeagueParams?.region;
        delete updatedLeagueParams?.image;
        await dispatch(editLeague(updatedLeagueParams));
      }
    } catch (e) {
      setError({ action: true, text: "An error has occurred while maintaining the league, check the data in the form and try again!" });
    }
  
    setOpen(true);
  };

  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };
  
  useEffect(() => {
    (async () => {
      const regionList = await getAvailableRegions();
      setOptions({...options, region: regionList});
    })();
  }, []);
  
  const handleValueFilling = (value: any, valueType: any) => {
    switch (valueType) {
      case 'region': {
        return value?.name;
      }
      case 'createdDate': {
        return moment(value).format('LLL');
      }
      default: return value;
    }
  }
  
  const handleOptionsFilling = (value: any, valueType: any) => {
    switch (valueType) {
      case 'color': {
        return (
          value?.map((item: string) => (
            <MenuItem className="!flex w-full !justify-between" value={item}>
              <div style={{ backgroundColor: item}} className="w-[40%] h-5 rounded-lg"/>
              <span className="uppercase block ml-2">{item}</span>
            </MenuItem>
          ))
        );
      }
      case 'isPaid': {
        return (
          value?.map((item: string) => (
            <MenuItem className="w-full" value={item}>
              {item ? <CheckCircleTwoToneIcon color="success"/> : <DoDisturbOnTwoToneIcon color="error"/>}
              <span className="ml-2">{item ? 'Paid' : 'Unpaid'}</span>
            </MenuItem>
          ))
        );
      }
      case 'activated': {
        return (
          value?.map((item: string) => (
            <MenuItem className="w-full" value={item}>
              {item ? <CheckCircleTwoToneIcon color="success" /> : <DoDisturbOnTwoToneIcon color="error"/>}
              <span className="ml-2">{item ? 'Active' : 'Inactive'}</span>
            </MenuItem>
          ))
        );
      }
      case 'type': {
        return (
          value?.map((item: string) => (
            <MenuItem className="w-full" value={item}>
              {item === 'championship' ?
                <EmojiEventsTwoToneIcon color="warning" /> :
                <SportsSoccerTwoToneIcon color="info" />
              }
              <span className="ml-2 uppercase">{item}</span>
            </MenuItem>
          ))
        );
      }
      case 'region': {
        return (
          value?.map((item: any) => (
            <MenuItem className="w-full" value={item?.name}>
              <span className="ml-2 uppercase">{item?.name}</span>
            </MenuItem>
          ))
        );
      }
      default: return (
        value.map((item: string) => (
          <MenuItem className="w-full" value={item}>{item}</MenuItem>
        ))
      )
    }
  }
  
  const handleUploadImage = async (files: any) => {
    try {
      const preSignedImage = await createPreSignedUrl(
        { type: "LEAGUE_LOGO", name: files[0].name, isPublic: true}
      );
      
      await fetch(preSignedImage.presignedUrl, {
        method: "PUT",
        body: files[0],
      });
  
      if (preSignedImage.id) setNewLeague({...newLeague, imageId: preSignedImage.id, imageUrl: preSignedImage.url });
    } catch (e) {
      console.log(e);
    }
  }
  
  const handleRemoveImage = async () => {
    try {
      setNewLeague({
        ...newLeague,
        image: null,
        imageId: null,
        imageUrl: null
      });
      // await deleteImage(newLeague?.imageId || newLeague?.image?.id)
    } catch (e) {
      console.log(e);
    }
  };
  
  const handleSelectImage = (selectImage: ImageType) => {
    setIsOpenFileSelect(false);
    setNewLeague({...newLeague, imageId: selectImage.id, imageUrl: selectImage.url })
  };
  
  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", marginTop: 15 }}>
        {location.state && options &&
          Object.entries(newLeague ? newLeague : location.state).map((item, index) => {
            const canView = item[0] !== 'image' && item[0] !== 'imageId'
              && item[0] !== 'imageUrl' && item[0] !== 'id' && item[0] !== 'createdDate';
            
            return (canView &&
              <div key={index.toString()} style={{ marginBottom: 15, marginRight: 15 }}>
                {freeSoloOptions.includes(item[0]) ? (
                  <Autocomplete
                    className={clsx('hover:cursor-pointer select-none capitalize', styles.autoComplete)}
                    freeSolo={freeSoloOptions.includes(item[0])}
                    disablePortal
                    autoComplete={true}
                    disabled={item[0] === "id" || item[0] === "createdDate" || item[0] === "matchesCount"}
                    id="combo-box-demo"
                    options={options[item[0]] ?? [""]}
                    onChange={(event, value) => {
                      handleChangeValue(value, item[0]);
                    }}
                    sx={{ width: 300, padding: 0 }}
                    value={handleValueFilling(newLeague[item[0]], item[0])}
                    onSelect={(event) => {
                      handleChangeValue(event, item[0]);
                    }}
                    onInput={(event) => {
                      if (item[0] !== "type") {
                        handleChangeValue(event, item[0]);
                      }
                    }}
                    renderInput={(params) =>
                      <TextField
                        {...params}
                        type={(item[0] === 'price' || item[0] === 'sortPriority') ? 'number' : undefined}
                        className="hover:cursor-pointer select-none"
                        label={item[0]}
                        value={newLeague[item[0]]}
                        inputProps={item[0] === 'shortName' ? { maxLength: 5 } : {}}
                      />
                    }
                  />
                ) : (
                  <FormControl className={styles.selectFormContainer}>
                    <InputLabel id={`select-helper-label-${item[0]}`} className="px-15">{item[0]}</InputLabel>
                    <Select
                      labelId={`select-helper-label-${item[0]}`}
                      id={`simple-select-${item[0]}`}
                      className={styles.selectListContainer}
                      value={handleValueFilling(newLeague[item[0]], item[0])}
                      label={item[0]}
                      onChange={(event) => handleChangeValue(event, item[0])}
                      placeholder={item[0]}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            width: 300
                          },
                        },
                      }}
                    >
                      {handleOptionsFilling(options[item[0]] ?? ["something"], item[0])}
                    </Select>
                  </FormControl>
                )}
              </div>
            );
          })}
        <div className="w-[50%] flex items-center mb-5">
          <FormControl fullWidth>
            <InputLabel id="select-button-file">Select image</InputLabel>
            <Select
              disabled={newLeague?.image?.url || newLeague?.imageUrl}
              labelId="select-button-file"
              id="select-file-component"
              value={null}
              label="Select image"
              open={isOpenFileSelect}
              onOpen={() => setIsOpenFileSelect(true)}
              onClose={() => setIsOpenFileSelect(false)}
            >
              <Grid container spacing={0} className="max-w-[550px] max-h-[150px] overflow-auto">
                {availableImages.map((item: any) => (
                  <Grid item md={0}>
                    <MenuItem value={item.id} onClick={() => handleSelectImage(item)}>
                      <Avatar variant="rounded" src={item.url}>
                        <ImageTwoToneIcon />
                      </Avatar>
                      <span>{item.label}</span>
                    </MenuItem>
                  </Grid>
                ))}
              </Grid>
            </Select>
          </FormControl>
          <p className="mt-2 mb-2 mr-5 ml-5 text-xs text-center font-bold text-gray">OR</p>
          <Button
            disabled={newLeague?.image?.url || newLeague?.imageUrl}
            variant="contained"
            component="label"
            style={{ padding: "10px 10px", backgroundColor: "rgb(241, 243, 244)", color: "rgb(25, 118, 210)", width: '100%' }}
            className="w-max"
          >
            Upload photo
            <input
              accept="image/*"
              style={{ display: "none" }}
              id="active-button-file"
              multiple
              type="file"
              onChange={(event) =>
                handleUploadImage(event.target.files)
              }
            />
          </Button>
        </div>
      </div>
      {(newLeague?.image?.url || newLeague?.imageUrl) && (
        <div className="w-max mb-5">
          <span className="text-[#a1a1a1] text-sm">Image preview</span>
          <img
            src={newLeague?.imageUrl || newLeague?.image?.url || ''}
            alt="imagePreview"
            className="w-[30%] h-auto p-2 border border-[#a1a1a1] rounded-lg mt-1"
          />
          <Button className="!mt-3" variant="outlined" color="error" onClick={handleRemoveImage}>
            Remove picture
          </Button>
        </div>
      )}
      <Button
        onClick={() => navigate('/leagues')}
        className="!py-3 !px-6 !mr-5"
        variant="outlined"
      >
        Back
      </Button>
      <Button onClick={handleSave} className="!py-3 !px-7" variant="contained">
        Save
      </Button>
      <Snackbar
        sx={{ height: "5%" }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        open={open}
        autoHideDuration={5000}
        onClose={handleClose}
      >
        <Alert onClose={handleClose} severity={!error.action ? "success" : "error"} sx={{ width: "100%" }}>
          {!error.action ? "League is successfully saved." : error.text}
        </Alert>
      </Snackbar>
    </div>
  );
}
